<template>
  <div class="issue-report" role="status">
    <template v-if="reported">
      <span class="issue-report__success">Reported. We’ll take a look.</span>
    </template>
    <template v-else>
      <span>{{ message }}</span>
      <button
        type="button"
        class="issue-report__button"
        :humblytics="`issue-report-${surface}`"
        @click="openReportModal"
      >
        Report it
      </button>
      <span>and we’ll resolve it.</span>
    </template>

    <teleport to="body">
      <div
        v-if="showModal"
        class="issue-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Report an issue"
        @click.self="closeReportModal"
        @keydown.esc="closeReportModal"
      >
        <form class="issue-modal__card" @submit.prevent="reportIssue">
          <div class="issue-modal__header">
            <div>
              <p class="issue-modal__eyebrow">Report an issue</p>
              <h2>Help us resolve this</h2>
            </div>
            <button type="button" class="issue-modal__close" aria-label="Close" @click="closeReportModal">×</button>
          </div>

          <p class="issue-modal__intro">
            We captured the page and error details. Add anything else that might help us understand what happened.
          </p>

          <label class="issue-modal__label" for="issue-report-notes">Notes <span>Optional</span></label>
          <textarea
            id="issue-report-notes"
            ref="notesInput"
            v-model="userNotes"
            class="issue-modal__textarea"
            maxlength="2000"
            rows="5"
            placeholder="What were you trying to do? Anything else you noticed?"
          ></textarea>
          <div class="issue-modal__count">{{ userNotes.length }}/2000</div>

          <p v-if="reportError" class="issue-modal__error">{{ reportError }}</p>

          <div class="issue-modal__actions">
            <button type="button" class="issue-modal__cancel" :disabled="reporting" @click="closeReportModal">Cancel</button>
            <button type="submit" class="issue-modal__submit" :disabled="reporting">
              {{ reporting ? 'Submitting…' : 'Submit report' }}
            </button>
          </div>
        </form>
      </div>
    </teleport>
  </div>
</template>

<script>
import api from '@/services/api.js';
import { captureError } from '@/services/errorReporting.js';

export default {
  name: 'IssueReportPrompt',
  props: {
    issueType: { type: String, required: true },
    surface: { type: String, required: true },
    resourceType: { type: String, default: null },
    resourceId: { type: [String, Number], default: null },
    errorMessage: { type: String, default: null },
    metadata: { type: Object, default: () => ({}) },
    message: {
      type: String,
      default: 'We may have an issue.',
    },
  },
  data() {
    return {
      reporting: false,
      reported: false,
      reportError: null,
      showModal: false,
      userNotes: '',
    };
  },
  watch: {
    errorMessage: {
      immediate: true,
      handler(message) {
        if (message) captureError(message, { source: 'browser', component: this.surface, code: this.issueType });
      },
    },
  },
  methods: {
    openReportModal() {
      this.reportError = null;
      this.showModal = true;
      this.$nextTick(() => this.$refs.notesInput?.focus());
    },
    closeReportModal() {
      if (this.reporting) return;
      this.showModal = false;
      this.reportError = null;
    },
    async reportIssue() {
      if (this.reporting || this.reported) return;
      this.reporting = true;
      this.reportError = null;
      try {
        await api.post('/issues/report', {
          issueType: this.issueType,
          surface: this.surface,
          resourceType: this.resourceType,
          resourceId: this.resourceId,
          pageUrl: typeof window !== 'undefined' ? window.location.href : null,
          errorMessage: this.errorMessage,
          userNotes: this.userNotes.trim() || null,
          metadata: this.metadata,
        });
        this.reported = true;
        this.showModal = false;
      } catch (error) {
        this.reportError = error.response?.data?.error || 'Could not report this issue. Please try again.';
      } finally {
        this.reporting = false;
      }
    },
  },
};
</script>

<style scoped>
.issue-report {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.45;
  color: var(--ink-500, #5f6470);
}

.issue-report__button {
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--accent, #315efb);
  font: inherit;
  font-weight: 650;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}

.issue-report__button:disabled {
  cursor: wait;
  opacity: 0.65;
}

.issue-report__success {
  color: var(--success-600, #27830f);
  font-weight: 600;
}

.issue-report__error {
  flex-basis: 100%;
  color: #b91c1c;
}

.issue-modal {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(4, 26, 48, 0.55);
  backdrop-filter: blur(3px);
}

.issue-modal__card {
  width: min(100%, 480px);
  padding: 24px;
  border: 1px solid var(--cream-500, #ddd4cc);
  border-radius: 18px;
  background: var(--paper, #fff);
  color: var(--ink-800, #18202a);
  box-shadow: 0 24px 70px rgba(4, 26, 48, 0.24);
}

.issue-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

.issue-modal__eyebrow {
  margin: 0 0 5px;
  color: var(--ink-400, #747a82);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.issue-modal h2 {
  margin: 0;
  color: var(--ink-900, #111820);
  font-size: 22px;
  line-height: 1.2;
}

.issue-modal__close {
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 9px;
  background: var(--cream-200, #f3eee9);
  color: var(--ink-600, #414852);
  font-size: 23px;
  line-height: 1;
  cursor: pointer;
}

.issue-modal__intro {
  margin: 16px 0 20px;
  color: var(--ink-500, #5f6470);
  font-size: 14px;
  line-height: 1.5;
}

.issue-modal__label {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  color: var(--ink-700, #29313b);
  font-size: 13px;
  font-weight: 650;
}

.issue-modal__label span,
.issue-modal__count {
  color: var(--ink-400, #858a91);
  font-size: 11px;
  font-weight: 500;
}

.issue-modal__textarea {
  display: block;
  width: 100%;
  resize: vertical;
  min-height: 120px;
  padding: 12px 14px;
  border: 1px solid var(--cream-500, #d8d0c8);
  border-radius: 11px;
  background: var(--paper, #fff);
  color: var(--ink-800, #18202a);
  font: inherit;
  font-size: 14px;
  line-height: 1.5;
  box-sizing: border-box;
}

.issue-modal__textarea:focus {
  outline: 2px solid color-mix(in srgb, var(--accent, #315efb) 25%, transparent);
  border-color: var(--accent, #315efb);
}

.issue-modal__count {
  margin-top: 5px;
  text-align: right;
}

.issue-modal__error {
  margin: 10px 0 0;
  color: #b91c1c;
  font-size: 12px;
}

.issue-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.issue-modal__cancel,
.issue-modal__submit {
  padding: 10px 17px;
  border-radius: 999px;
  font: inherit;
  font-size: 13px;
  font-weight: 650;
  cursor: pointer;
}

.issue-modal__cancel {
  border: 1px solid var(--cream-500, #d8d0c8);
  background: transparent;
  color: var(--ink-700, #29313b);
}

.issue-modal__submit {
  border: 1px solid var(--ink-900, #111820);
  background: var(--ink-900, #111820);
  color: #fff;
}

.issue-modal__cancel:disabled,
.issue-modal__submit:disabled {
  cursor: wait;
  opacity: 0.65;
}
</style>
